import { watch } from 'vue';
import MixpanelPluginV2 from '@satellite/plugins/MixpanelPluginV2.js';
import { useAnalytics, type AnalyticsClient } from '@satellite/composables/useAnalytics';
import { useYardTaskAnalytics } from '@/analytics/yardTaskEvents';
import { useAuthStore } from '@/store/useAuthStore';
import { useWarehouseStore } from '@/store/useWarehouseStore';
import config from '@/config/config';
import type { IMeResponse } from '@/types/apiTypes';
import { isEmpty } from 'lodash';

export function createCirceAnalyticsClient(): AnalyticsClient {
  const mixpanel = new MixpanelPluginV2({
    token: config.mixpanel_token ?? null,
    enabled: Boolean(config.mixpanel_token),
    debug: config.server_env === 'development'
  });
  mixpanel.init();

  return {
    track: (event, payload) =>
      mixpanel.track(String(event), !isEmpty(payload) ? payload : undefined),
    identify: value => {
      if (typeof value !== 'string' || !value) {
        return;
      }
      mixpanel.identify(value);
    },
    reset: () => mixpanel.reset(),
    registerSuper: props => mixpanel.setProperties(props),
    setUserProperties: props => mixpanel.setUserProperties(props)
  };
}

export function useCirceAnalytics() {
  const { trackYardTaskWarehouseChanged } = useYardTaskAnalytics();
  const analytics = useAnalytics();
  const authStore = useAuthStore();
  const warehouseStore = useWarehouseStore();

  const registerOrgSuperProperty = () => {
    const user = authStore.user as IMeResponse | null;
    if (!user) {
      return;
    }

    analytics.registerSuper({
      org_id: user.orgId ?? '',
      org_name: user.orgName ?? ''
    });
  };

  const registerWarehouseSuperProperty = () => {
    const user = authStore.user as IMeResponse | null;
    if (!user) {
      return;
    }

    const selectedWarehouseId = warehouseStore.selectedWarehouseId;
    const selectedWarehouseName = warehouseStore.selectedWarehouseName;

    analytics.registerSuper({
      warehouse_id: String(selectedWarehouseId ?? ''),
      warehouse_name: String(selectedWarehouseName ?? '')
    });
  };

  const registerUserSuperProperty = () => {
    const user = authStore.user as IMeResponse | null;
    if (!user) {
      return;
    }
    analytics.setUserProperties({
      $distinct_id: user.id ?? '',
      $email: user.email ?? '',
      $name: `${user.firstName ?? ''} ${user.lastName ?? ''}`,
      user_role: user.role ?? '',
      org_id: user.orgId ?? '',
      org_name: user.orgName ?? ''
    });
  };

  watch(
    () => authStore.user,
    user => {
      if (user) {
        analytics.identify(user.id);
        registerOrgSuperProperty();
        registerWarehouseSuperProperty();
        registerUserSuperProperty();
      } else {
        analytics.reset();
      }
    },
    { immediate: true }
  );

  watch(
    () => warehouseStore.selectedWarehouseId,
    () => {
      registerWarehouseSuperProperty();
      trackYardTaskWarehouseChanged();
    }
  );
}
